/**
 * resolveMovimientoCajaLink.ts — Resuelve el origen enlazable de un movimiento de caja.
 *
 * Centraliza el parseo de ventaId/creditoId desde movimientos (devolución, anulación, abono a crédito)
 * para deep-links reutilizables desde Movimientos, inventario y cierres.
 *
 * NO debe importar React Router ni disparar navegación.
 */

import type { Credito, MovimientoCaja, Venta } from "../types/caja.types";
import {
  clienteNombreAbonoCredito,
  isMovimientoAbonoCredito,
} from "./movimientoAbonoCredito";
import { ventaHistorialFechaFromIso } from "./navigateToVentaHistorial";

export type MovimientoCajaLinkKind = "venta" | "credito";

export type MovimientoCajaLink = {
  kind: MovimientoCajaLinkKind;
  ventaId?: string;
  creditoId?: string;
  movimientoId: string;
  fecha: string;
  label: string;
};

export function parseDevolucionMotivo(motivo: string): {
  devId: string;
  motivoCorto: string;
} | null {
  const m = motivo.match(/^Devolución (#DEV-\d+):\s*(.+)$/i);
  if (!m) return null;
  return { devId: m[1].replace("#", ""), motivoCorto: m[2].trim() };
}

export function parseVentaIdFromAnulacionMotivo(motivo: string): string | null {
  const m = motivo.match(
    /venta\s+(T-[\w-]+|v-[\w-]+|may12-[\w-]+|demo-[\w-]+)/i,
  );
  return m?.[1] ?? null;
}

/** Venta POS vinculada a devolución o anulación (por campo o motivo). */
export function findVentaForMovimiento(
  mov: MovimientoCaja,
  ventas: Venta[],
): Venta | undefined {
  if (mov.ventaId) {
    const byId = ventas.find((v) => v.id === mov.ventaId);
    if (byId) return byId;
  }
  const dev = parseDevolucionMotivo(mov.motivo || "");
  if (dev) {
    return ventas.find((v) =>
      (v.devoluciones ?? []).some((d) => d.id === dev.devId),
    );
  }
  const ventaId = parseVentaIdFromAnulacionMotivo(mov.motivo || "");
  if (ventaId) return ventas.find((v) => v.id === ventaId);
  return undefined;
}

export function findCreditoForMovimiento(
  mov: MovimientoCaja,
  creditos: Credito[],
): Credito | undefined {
  if (mov.creditoId) return creditos.find((f) => f.id === mov.creditoId);
  return undefined;
}

/** Venta POS original vinculada a un abono de cuenta de crédito. */
export function findVentaForCreditoAbono(
  mov: MovimientoCaja,
  ventas: Venta[],
  creditos: Credito[] = [],
): Venta | undefined {
  const creditoId = mov.creditoId?.trim();
  if (creditoId) {
    const byCreditoId = ventas.find((v) => v.creditoId === creditoId);
    if (byCreditoId) return byCreditoId;
  }

  const credito = findCreditoForMovimiento(mov, creditos);
  const clienteMotivo = clienteNombreAbonoCredito(mov.motivo || "")
    .trim()
    .toLowerCase();

  const ventasCreditos = ventas.filter(
    (v) =>
      v.metodoPago === "credito" ||
      Boolean(v.creditoId) ||
      Boolean(v.clienteCreditoNombre?.trim()),
  );

  if (credito) {
    const byCredito = ventasCreditos.find((v) => v.creditoId === credito.id);
    if (byCredito) return byCredito;

    const clienteCredito = credito.clienteNombre.trim().toLowerCase();
    const byClienteTotal = ventasCreditos.find(
      (v) =>
        v.clienteCreditoNombre?.trim().toLowerCase() === clienteCredito &&
        Math.abs(v.total - credito.total) < 1,
    );
    if (byClienteTotal) return byClienteTotal;
  }

  if (clienteMotivo && clienteMotivo !== "cliente") {
    const byMotivo = ventasCreditos.filter(
      (v) => v.clienteCreditoNombre?.trim().toLowerCase() === clienteMotivo,
    );
    if (byMotivo.length === 1) return byMotivo[0];
    if (credito && byMotivo.length > 1) {
      return byMotivo.find((v) => Math.abs(v.total - credito.total) < 1);
    }
  }

  return undefined;
}

export function resolveMovimientoCajaLink(
  mov: MovimientoCaja,
  ventas: Venta[],
  creditos: Credito[] = [],
): MovimientoCajaLink | null {
  const fecha = ventaHistorialFechaFromIso(mov.fecha);

  if (isMovimientoAbonoCredito(mov)) {
    const venta = findVentaForCreditoAbono(mov, ventas, creditos);
    if (!venta) return null;
    return {
      kind: "venta",
      ventaId: venta.id,
      movimientoId: mov.id,
      fecha: ventaHistorialFechaFromIso(venta.fecha),
      label: "Ver Caja",
    };
  }

  const esReversal =
    mov.tipo === "devolucion" ||
    mov.tipo === "anulacion" ||
    (mov.tipo === "gasto" && mov.motivo?.startsWith("Devolución #DEV-"));

  if (!esReversal) return null;

  const venta = findVentaForMovimiento(mov, ventas);
  if (!venta) return null;

  return {
    kind: "venta",
    ventaId: venta.id,
    movimientoId: mov.id,
    fecha,
    label: "Ver Caja",
  };
}
